import { Component, ReactNode, ErrorInfo } from 'react';
import { posthog, isPostHogConfigured } from '../lib/posthog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary to catch React rendering errors
 * Automatically sends errors to PostHog for monitoring
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Send error to PostHog
    if (isPostHogConfigured()) {
      posthog.capture('react_error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    
    // Reload the page to reset state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: 'var(--space-xl)',
            textAlign: 'center',
            background: 'var(--color-bg-primary)',
          }}
        >
          <div
            style={{
              maxWidth: 400,
              padding: 'var(--space-xl)',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 48,
                marginBottom: 'var(--space-md)',
              }}
            >
              😵
            </div>
            <h1
              style={{
                fontSize: 'var(--font-xl)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Algo deu errado
            </h1>
            <p
              style={{
                fontSize: 'var(--font-md)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-lg)',
              }}
            >
              Desculpe, encontramos um erro inesperado. Tente recarregar a página.
            </p>
            {this.state.error && (
              <details
                style={{
                  marginBottom: 'var(--space-lg)',
                  textAlign: 'left',
                  fontSize: 'var(--font-sm)',
                  color: 'var(--color-text-tertiary)',
                  background: 'var(--color-bg-primary)',
                  padding: 'var(--space-sm)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    marginBottom: 'var(--space-xs)',
                    fontWeight: 500,
                  }}
                >
                  Detalhes técnicos
                </summary>
                <code
                  style={{
                    display: 'block',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.message}
                </code>
              </details>
            )}
            <button
              onClick={this.handleReset}
              style={{
                width: '100%',
                padding: 'var(--space-sm) var(--space-md)',
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-md)',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              Recarregar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

